import type { BimcCalculationLimitedCostData } from 'gql/graphql';
import { ChapterEnum } from 'gql/graphql';

import * as R from 'ramda';
import { useMemo } from 'react';

export interface IUseBimCalculationLimitedCostsGrouped {
    items?: BimcCalculationLimitedCostData[] | null | undefined;
    constructionTypeId?: number;
}

export const useBimCalculationLimitedCostsGrouped = (props: IUseBimCalculationLimitedCostsGrouped) => {
    const { items, constructionTypeId } = props;

    const rows = useMemo(
        () =>
            items?.map((item: BimcCalculationLimitedCostData) => {
                const seasonAmounts = (item.seasonAmounts ?? []).filter(
                    (sa) => !constructionTypeId || sa.constructionTypeId === constructionTypeId,
                );

                const mappedSeasons = seasonAmounts.reduce((a: Record<string, number>, sl) => {
                    a[`season${sl.seasonId}`] = (a[`season${sl.seasonId}`] ?? 0) + sl.cost;

                    return a;
                }, {});

                const totalCost = seasonAmounts.length
                    ? seasonAmounts.reduce((sum: number, sl) => sum + Number(sl.cost ?? 0), 0)
                    : undefined;

                return {
                    ...item,
                    ...mappedSeasons,
                    totalCost,
                };
            }),
        [constructionTypeId, items],
    );

    const rowsGrouped = useMemo(() => {
        const rowsBySection = R.groupBy(R.prop('chapter'), rows || []);
        const chapter17Data = rowsBySection[ChapterEnum.Chapter1_7] ?? [];
        const chapter8Data = rowsBySection[ChapterEnum.Chapter8] ?? [];
        const chapter9Data = rowsBySection[ChapterEnum.Chapter9] ?? [];

        const chapter9DataAndHeader = chapter9Data.length
            ? [{ id: ChapterEnum.Chapter9, name: 'Глава 9' }, ...chapter9Data]
            : [];

        const chapter8DataAndHeader = chapter8Data.length
            ? [{ id: ChapterEnum.Chapter8, name: 'Глава 8' }, ...chapter8Data]
            : [];

        const chapter17DataAndHeader = chapter17Data.length
            ? [{ id: ChapterEnum.Chapter1_7, name: 'Глава 1-7' }, ...chapter17Data]
            : [];

        return [...chapter17DataAndHeader, ...chapter8DataAndHeader, ...chapter9DataAndHeader];
    }, [rows]);

    const itemsCount = useMemo(() => items?.length || 0, [items?.length]);

    return { rowsGrouped, itemsCount };
};
